import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { makeStyles } from '@material-ui/core/styles';

import { pantonFonts } from 'components/theme/common';
import { getRedesignDialogColors } from './redesignDialogColors';

/** The room between two sections of a dialog's body. */
export const REDESIGN_DIALOG_SECTION_GAP = 24;

const useStyles = makeStyles(
  (theme) => {
    const colors = getRedesignDialogColors(theme);
    return {
      section: {
        margin: 0,
        '& + &': {
          marginTop: REDESIGN_DIALOG_SECTION_GAP,
        },
      },
      header: {
        marginBottom: theme.spacing(1.5),
      },
      // The title and its actions on one line.
      titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        minWidth: 0,
      },
      title: {
        margin: 0,
        minWidth: 0,
        fontFamily: pantonFonts.panton,
        fontWeight: 'normal',
        fontSize: '1rem',
        lineHeight: 1.3,
        color: colors.text,
      },
      description: {
        margin: theme.spacing(0.5, 0, 0),
        fontFamily: pantonFonts.panton,
        fontSize: '0.8125rem',
        lineHeight: 1.45,
        color: colors.muted,
      },
      // Centred on the title's line and taking no more height than it: a
      // 36px icon button hangs over the line by the same amount each side.
      actions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        flexShrink: 0,
        marginLeft: 'auto',
        marginTop: -8,
        marginBottom: -8,
      },
    };
  },
  { name: 'LixRedesignDialogSection' }
);

let sectionCount = 0;
const nextSectionId = () => {
  sectionCount += 1;
  return `lix-redesign-dialog-section-${sectionCount}`;
};

/**
 * A titled group in a redesigned dialog's body (or a split's pane): `title`,
 * optional `actions` at the right of the title (RedesignDialogIconButtons, a
 * small RedesignDialogButton), an optional `description` under it, then
 * `children`. Consecutive sections keep a 24px rhythm. The section is a
 * region named by its title; without a title it is only the group. Other
 * props (data-*) go to the <section>.
 * @example
 * <RedesignDialogSection
 *   title={t('expense.invoice_company_details')}
 *   actions={<RedesignDialogIconButton icon={mdiPlus} aria-label={t('common.add')} onClick={add} />}
 * >
 *   <TextInput rounded fullWidth label={t('expense.invoice_company_name')} value={name} onChange={onName} />
 * </RedesignDialogSection>
 * @param {Object} props
 * @param {React.ReactNode} [props.title]
 * @param {React.ReactNode} [props.description]
 * @param {React.ReactNode} [props.actions]
 * @param {React.ReactNode} props.children
 */
function RedesignDialogSection({
  title,
  description,
  actions,
  className,
  children,
  ...rest
}) {
  const classes = useStyles();
  const [titleId] = useState(nextSectionId);
  const hasTitleRow = Boolean(title || actions);
  return (
    <section
      className={clsx(classes.section, className)}
      aria-labelledby={title ? titleId : undefined}
      data-redesign-dialog-section="true"
      {...rest}
    >
      {hasTitleRow || description ? (
        <div className={classes.header}>
          {hasTitleRow ? (
            <div className={classes.titleRow}>
              {title ? (
                <h3 id={titleId} className={classes.title}>
                  {title}
                </h3>
              ) : null}
              {actions ? (
                <div className={classes.actions}>{actions}</div>
              ) : null}
            </div>
          ) : null}
          {description ? (
            <p className={classes.description}>{description}</p>
          ) : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

RedesignDialogSection.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  /** At the right of the title. */
  actions: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default RedesignDialogSection;
